import { ArrowLeft, Upload, Plus, UserPlus, X, FileText } from 'lucide-react'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useCenterStore } from '@caradasstore/CenterStore'
import { useMessageStore } from '@caradasstore/MessageStore'
import { CarImageHolder } from '@components/car-image-holder'
import { ClientForm } from '@components/client'
import { VehicleInfoDetails } from '@components/vehicle-info-details'
import { VinInput } from '@components/vin'
import {
  AdasBox,
  AdasButton,
  AdasContainer,
  AdasRadioGroup,
  AdasSelect,
  AdasTextField,
  AdasTypography,
  AdasAutoComplete
} from '@components/wrapper-components'
import {
  requestCreateWO,
  useSubmitFileAttachment,
  getAttachmentAppointmentEstimate,
  getAttachmentAppointmentCalibration
} from '../../../api/api'
import { VehicleInfoManuallPage } from '../../vehicle-info-page/VehicleInfoManuallPage'
import { getWorkOrderSummaryUrl } from '../../../constants'
import { ATTACHMENT_TYPE_MAP, MIME_TYPES_MAP } from '../../../variable-maps'
import { Logger } from '../../../logger'
import { fuzzyMatchClients, matchWithFuzzy } from '../../../utils/fuzzyMatchClients'
import { useClientAndInsuranceData } from '@hooks/useClientAndInsuranceData'
import { useInitReferralAndInsuranceFromState } from '@hooks/useInitReferralAndInsuranceFromState'
import { useVehicleInProgress } from '@hooks/useVehicleInProgress'
import { FormModal } from '@components/branded-modal'
import { getFriendlyErrorMessage } from '../../../utils/errorMessage'

const StepBadge = ({ n }) => (
  <span className='inline-flex h-7 w-7 flex-none items-center justify-center rounded-full bg-[#0A1628] text-[12px] font-bold text-white'>
    {n}
  </span>
)
StepBadge.propTypes = { n: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) }

const FieldLabel = ({ children, required }) => (
  <AdasTypography className='mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>
    {children}
    {required && <span className='ml-0.5 text-[#E24B4A]'>*</span>}
  </AdasTypography>
)
FieldLabel.propTypes = { children: PropTypes.node, required: PropTypes.bool }

const DocumentUploadRow = ({ name, title, subtitle, existingData, onChange }) => {
  const fileRef = useRef(null)
  const addMessage = useMessageStore((store) => store.addMessage)

  const handleUpload = () => {
    const file = fileRef.current?.files?.[0]
    if (!file) return
    const supported = Object.values(MIME_TYPES_MAP)
    if (!supported.includes(file.type)) {
      addMessage({ text: 'File format not supported!' })
      return
    }
    onChange({ data: file, name })
  }

  const handleRemove = () => onChange({ data: '', name })

  return (
    <AdasBox className='flex items-center gap-3 rounded-lg border border-[#EBEDF0] bg-white px-4 py-3'>
      <AdasBox className='flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-[#E8EFFF]'>
        {existingData ? <FileText className='h-4 w-4 text-[#1F6FEB]' /> : <Upload className='h-4 w-4 text-[#1F6FEB]' />}
      </AdasBox>
      <AdasBox className='min-w-0 flex-1'>
        {existingData ? (
          <>
            <a
              href={URL.createObjectURL(existingData)}
              target='_blank'
              rel='noopener noreferrer'
              className='block truncate text-[14px] font-semibold text-[#0A1628] hover:underline'
            >
              {existingData.name}
            </a>
            <AdasTypography className='truncate text-[12px] text-[#6B7280]'>{subtitle}</AdasTypography>
          </>
        ) : (
          <>
            <AdasTypography className='text-[14px] font-semibold text-[#0A1628]'>{title}</AdasTypography>
            <AdasTypography className='text-[12px] text-[#6B7280]'>{subtitle}</AdasTypography>
          </>
        )}
      </AdasBox>
      {existingData ? (
        <button
          type='button'
          onClick={handleRemove}
          aria-label='Remove file'
          className='inline-flex h-8 w-8 items-center justify-center rounded-md text-[#475569] hover:bg-[#F8FAFC]'
        >
          <X className='h-4 w-4' />
        </button>
      ) : (
        <label className='inline-flex cursor-pointer items-center justify-center rounded-full bg-[#1F6FEB] px-4 py-1.5 text-[13px] font-bold uppercase tracking-wide text-white transition-colors hover:bg-[#1B5DD0]'>
          Upload
          <input ref={fileRef} type='file' accept='.jpg, .jpeg, .png, .pdf, .doc, .docx, .txt' hidden onChange={handleUpload} />
        </label>
      )}
    </AdasBox>
  )
}
DocumentUploadRow.propTypes = {
  name: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  existingData: PropTypes.object,
  onChange: PropTypes.func.isRequired
}

export const NewWorkOrderPage = () => {
  const [isAddClient, setIsAddClient] = useState(false)
  const [vehicleData, setVehicleData] = useState({})
  const [displayDetails, setDisplayDetails] = useState(false)
  const [customerPay, setCustomerPay] = useState(false)
  const [isVinAvailable, setIsVinAvailable] = useState(true)
  const [documents, setDocuments] = useState({ preCalRepairEstimateScan: '', calRequiredReport: '' })
  const location = useLocation()

  const prePopulateVin = location.state?.prepopulateVin || location.state?.vin
  const [formInputValues, setFormInputValues] = useState({
    client: '',
    roNumber: '',
    serviceType: 'STANDARD',
    vin: '',
    vehicleYear: '',
    vehicleMake: '',
    vehicleModel: '',
    vehicleTrim: '',
    insurance_company_id: '',
    appointment_id: '',
    referral_source_code: location.state?.referral || null
  })
  const { uploadData } = useSubmitFileAttachment()
  const ccId = useCenterStore((store) => store.ccId)
  const addMessage = useMessageStore((store) => store.addMessage)
  const { clients, insuranceCompanies, fetchClients, clearVehicleFields, handleVehicleManualChange } = useClientAndInsuranceData(ccId)

  // When creating a WO from a scheduled appointment, carry the insurer over by
  // matching the appointment's referral source to an insurance company (mirrors
  // New Quote's behavior).
  useInitReferralAndInsuranceFromState({
    referralSourceCode: location.state?.referral,
    insuranceCompanies,
    setFormInputValues,
    ccId
  })

  const isEnable = useMemo(() => {
    const vinValid = isVinAvailable
      ? !!formInputValues.vin && !!formInputValues.client
      : !!formInputValues.client &&
        !!formInputValues.vehicleYear &&
        !!formInputValues.vehicleMake &&
        !!formInputValues.vehicleModel &&
        !!formInputValues.vehicleTrim
    const roNumberValid = customerPay || formInputValues.roNumber
    const isInsuranceCompanyValid = !!formInputValues.insurance_company_id
    return vinValid && roNumberValid && isInsuranceCompanyValid
  }, [
    isVinAvailable,
    formInputValues.vin,
    formInputValues.client,
    formInputValues.vehicleYear,
    formInputValues.vehicleMake,
    formInputValues.vehicleModel,
    formInputValues.vehicleTrim,
    formInputValues.roNumber,
    customerPay,
    formInputValues.insurance_company_id
  ])

  const uploadFile = useCallback(
    async (file, type, id) => {
      const formData = new FormData()
      formData.append('file', file, file.name)
      const attachmentType = ATTACHMENT_TYPE_MAP[type]
      uploadData({ data: formData, workorderId: id, type: 'workorders', attachmentType })
    },
    [uploadData]
  )

  const history = useHistory()
  const redirectWorkOrderSummary = useCallback(
    (data) => {
      history.push(getWorkOrderSummaryUrl(data?.workOrder?.reference_number ?? data?.reference_number))
    },
    [history]
  )

  const documentChangeHandler = useCallback((props) => {
    setDocuments((prev) => ({ ...prev, [props.name]: props.data }))
  }, [])

  const changeHandler = useCallback((e) => {
    const fieldValue = e.target.name === 'year' ? e.target.value.replace(/\D/g, '') : e.target.value
    setFormInputValues((prev) => ({ ...prev, [e.target.name]: fieldValue }))
  }, [])

  const handleCustomerPay = useCallback((e) => {
    const value = /true/.test(e.target.value)
    const roNumber = value ? 'Customer-Pay' : ''
    setCustomerPay(value)
    setFormInputValues((prev) => ({ ...prev, roNumber }))
  }, [])

  const getVehicleInfo = useCallback(() => {
    if (isVinAvailable) {
      const { color, imageUrl, fuel, safetySystems, ...vehicle } = vehicleData
      return {
        vin: formInputValues.vin,
        calibration_center_id: ccId,
        exterior_color: color,
        image_url: imageUrl,
        fuel_capacity_gal: fuel,
        safetySystemsItems: safetySystems,
        ...vehicle
      }
    }
    const { vehicleYear, vehicleMake, vehicleModel, vehicleTrim } = formInputValues
    return { year: vehicleYear, make: vehicleMake, model: vehicleModel, trim: vehicleTrim, vin: '' }
  }, [ccId, formInputValues, isVinAvailable, vehicleData])

  const { handleCreateError, renderVehicleInProgress } = useVehicleInProgress('workOrder')

  const submitHandler = useCallback(async () => {
    const workOrderRequestData = {
      client_id: formInputValues.client,
      calibration_center_id: ccId,
      repair_order_num: formInputValues.roNumber,
      type: formInputValues.serviceType,
      insurance_company_id: formInputValues.insurance_company_id,
      appointment_id: formInputValues.appointment_id,
      vehicle: getVehicleInfo(),
      referral_source_code: formInputValues.referral_source_code
    }
    try {
      const response = await requestCreateWO({ data: workOrderRequestData })
      if (response?.status === 201) {
        const id = response.data.workOrder.id
        Object.keys(documents).forEach((doc) => {
          if (documents[doc]) uploadFile(documents[doc], doc, id)
        })
        redirectWorkOrderSummary(response.data)
      }
    } catch (err) {
      if (handleCreateError(err)) return
      Logger.error({ message: err, payload: { file: 'NewWorkOrderPage', method: 'submitHandler', workOrderRequestData } })
      addMessage({ text: getFriendlyErrorMessage(err) })
    }
  }, [
    addMessage,
    handleCreateError,
    ccId,
    documents,
    formInputValues.client,
    formInputValues.roNumber,
    formInputValues.serviceType,
    formInputValues.insurance_company_id,
    formInputValues.appointment_id,
    formInputValues.referral_source_code,
    getVehicleInfo,
    redirectWorkOrderSummary,
    uploadFile
  ])

  const autocompleteHandler = useCallback((event) => {
    const { name, value } = event.target
    setFormInputValues((prev) => ({ ...prev, [name]: value || '' }))
  }, [])

  const updateVehicleInfo = useCallback((props) => {
    setDisplayDetails(true)
    setVehicleData({ ...props.data, safetySystemsItems: props.data?.safetySystems })
    setFormInputValues((prev) => ({ ...prev, vin: props.vin }))
  }, [])

  useEffect(() => {
    if (location.state?.appointment_id || location.state?.aro) {
      setFormInputValues((prev) => ({
        ...prev,
        roNumber: location.state?.aro || prev.roNumber,
        appointment_id: location.state?.appointment_id || prev.appointment_id
      }))
    }
  }, [location.state?.appointment_id, location.state?.aro])

  // Pre-select the appointment's client once the client list has loaded — AdasSelect
  // (Radix) only reflects a value whose option is mounted. Match by saved client id
  // when present, otherwise by the appointment's business/full name (external
  // bookings carry the name, not a saved client id), with a fuzzy fallback.
  useEffect(() => {
    if (!clients?.length) return
    const clientId = location.state?.clientId
    const apptName = location.state?.businessName || location.state?.fullName || ''
    let match = clientId ? clients.find((c) => String(c.id) === String(clientId)) : null
    if (!match && apptName) {
      // Exact name, else a confident fuzzy hit sharing a distinctive word; otherwise leave it for the user.
      match = matchWithFuzzy(clients, apptName, fuzzyMatchClients) || null
    }
    if (match) setFormInputValues((prev) => (prev.client ? prev : { ...prev, client: match.id }))
  }, [location.state?.clientId, location.state?.businessName, location.state?.fullName, clients])

  // Link the documents attached on the appointment (estimate + calibration report)
  // into the WO's document slots, same as New Quote.
  useEffect(() => {
    const fetchEstimateFile = async (value) => {
      try {
        const response = await getAttachmentAppointmentEstimate(ccId, value.appointment_id)
        const fileFromResponse = new File([response.data], value.name, { type: MIME_TYPES_MAP[`.${value.file_extension}`] })
        fileFromResponse.id = value.id
        setDocuments((prev) => ({ ...prev, preCalRepairEstimateScan: fileFromResponse }))
      } catch (error) {
        Logger.error({ message: error.message, payload: { value, file: 'NewWorkOrderPage/fetchEstimateFile' } })
      }
    }
    const fetchCalibrationFile = async (value) => {
      try {
        const response = await getAttachmentAppointmentCalibration(ccId, value.appointment_id)
        const fileFromResponse = new File([response.data], value.name, { type: MIME_TYPES_MAP[`.${value.file_extension}`] })
        fileFromResponse.id = value.id
        setDocuments((prev) => ({ ...prev, calRequiredReport: fileFromResponse }))
      } catch (error) {
        Logger.error({ message: error.message, payload: { value, file: 'NewWorkOrderPage/fetchCalibrationFile' } })
      }
    }
    if (location.state?.recentEstimate?.id) fetchEstimateFile(location.state.recentEstimate)
    if (location.state?.calibrationFile?.id) fetchCalibrationFile(location.state.calibrationFile)
  }, [location.state?.recentEstimate?.id, location.state?.calibrationFile?.id])

  const handleVinAvailableToggle = useCallback((e) => {
    setIsVinAvailable(e.target.value === 'true')
    setDisplayDetails(false)
    setVehicleData({})
    setFormInputValues((prev) => ({ ...prev, vin: '' }))
  }, [])

  useEffect(() => {
    fetchClients()
  }, [fetchClients])

  const handleVehicleManuallChange = (eventOrValue) => {
    handleVehicleManualChange(eventOrValue, setFormInputValues)
  }

  useEffect(() => {
    clearVehicleFields(setFormInputValues)
  }, [isVinAvailable])

  return (
    <AdasContainer maxWidth='lg'>
      <AdasBox className='mb-[60px] mt-5'>
        {isAddClient && (
          <FormModal open={isAddClient} onClose={() => setIsAddClient(false)} title='Client Info' icon={UserPlus} width='max-w-[520px]'>
            <ClientForm onSaveSuccess={fetchClients} clibrationCenterId={Number(ccId)} close={() => setIsAddClient(false)} />
          </FormModal>
        )}

        {/* Page header — desktop only; mobile uses the global nav header + native back */}
        <AdasBox className='mb-6 hidden min-[435px]:block'>
          <button
            type='button'
            onClick={() => history.goBack()}
            className='mb-1 inline-flex items-center gap-1.5 text-[13px] font-medium text-[#1F6FEB] hover:underline'
          >
            <ArrowLeft className='h-4 w-4' />
            Back to Work Orders List
          </button>
          <AdasTypography className='text-[28px] font-bold leading-tight text-[#0A1628]'>New Work Order</AdasTypography>
        </AdasBox>

        {/* Step 1 — Client & repair order */}
        <AdasBox className='mb-4 rounded-xl border border-[#EBEDF0] bg-white px-6 py-5 shadow-sm'>
          <AdasBox className='mb-5 flex items-center gap-3 border-b border-[#EBEDF0] pb-4'>
            <StepBadge n={1} />
            <AdasTypography className='text-[15px] font-bold text-[#0A1628]'>Client &amp; repair order</AdasTypography>
          </AdasBox>

          <AdasBox className='mb-5'>
            <FieldLabel required>Client</FieldLabel>
            <AdasBox className='flex items-stretch w-full'>
              <AdasSelect
                labelId='clients'
                id='client-select'
                name='client'
                value={formInputValues.client}
                onChange={autocompleteHandler}
                options={clients || []}
                className='flex-1 min-w-0 text-left rounded-r-none border-r-0'
              />
              <button
                type='button'
                onClick={() => setIsAddClient(true)}
                aria-label='Add client'
                className='inline-flex items-center justify-center h-11 w-12 rounded-r-lg rounded-l-none bg-sidebar text-white hover:bg-sidebar/90 border border-l-0 border-sidebar transition-colors shrink-0'
              >
                <Plus className='h-5 w-5' />
              </button>
            </AdasBox>
          </AdasBox>

          <AdasBox className='mb-5'>
            <FieldLabel>Repair order</FieldLabel>
            <AdasRadioGroup
              row
              options={[
                { label: 'RO Provided', value: false },
                { label: 'Customer Pay', value: true }
              ]}
              name='clientPay'
              value={customerPay}
              onChange={handleCustomerPay}
            />
          </AdasBox>

          <AdasBox className='mb-5'>
            <FieldLabel required>RO #</FieldLabel>
            <AdasTextField
              id='outlined-basic'
              variant='outlined'
              name='roNumber'
              onChange={changeHandler}
              value={formInputValues.roNumber}
              disabled={customerPay}
              placeholder='RO #'
              inputProps={{ maxLength: 12 }}
              fullWidth
            />
          </AdasBox>

          <AdasBox className='mb-5'>
            <FieldLabel required>Insurance Company</FieldLabel>
            <AdasAutoComplete
              labelId='insurance_label'
              id='insurance_company_id'
              name='insurance_company_id'
              value={formInputValues.insurance_company_id}
              onChange={autocompleteHandler}
              options={insuranceCompanies || []}
              className='w-full text-left'
              placeholder='Insurance Company (Required)'
            />
          </AdasBox>

          <AdasBox>
            <FieldLabel>Type</FieldLabel>
            <AdasRadioGroup
              row
              options={[
                { value: 'STANDARD', label: 'Standard' },
                { value: 'SERVICE_ONLY', label: 'Service Only' }
              ]}
              name='serviceType'
              value={formInputValues.serviceType}
              onChange={changeHandler}
            />
          </AdasBox>
        </AdasBox>

        {/* Step 2 — Vehicle */}
        <AdasBox className='mb-4 rounded-xl border border-[#EBEDF0] bg-white px-6 py-5 shadow-sm'>
          <AdasBox className='mb-5 flex items-center gap-3 border-b border-[#EBEDF0] pb-4'>
            <StepBadge n={2} />
            <AdasTypography className='text-[15px] font-bold text-[#0A1628]'>Vehicle</AdasTypography>
          </AdasBox>

          <AdasBox className='mb-5'>
            <FieldLabel>Do you have a VIN?</FieldLabel>
            <AdasRadioGroup
              row
              options={[
                { label: 'Yes', value: 'true' },
                { label: 'No', value: 'false' }
              ]}
              aria-labelledby='controlled-radio-buttons-group'
              name='isVinAvailable'
              value={isVinAvailable}
              onChange={handleVinAvailableToggle}
            />
          </AdasBox>

          {isVinAvailable ? (
            <AdasBox key='vin-section'>
              <FieldLabel required>VIN</FieldLabel>
              <VinInput updateVehicleInfo={updateVehicleInfo} prepopulateVin={prePopulateVin} vinExistCheck={true} />
              {displayDetails ? (
                <VehicleInfoDetails data={vehicleData} />
              ) : (
                <AdasBox className='mt-4'>
                  <CarImageHolder showText={false} />
                  <AdasBox className='mt-4 rounded-lg bg-[#EBEDF0] py-3 text-center'>
                    <AdasTypography className='text-[13px] text-[#475569]'>Enter VIN to fill vehicle details</AdasTypography>
                  </AdasBox>
                </AdasBox>
              )}
            </AdasBox>
          ) : (
            <VehicleInfoManuallPage
              formInputValues={formInputValues}
              setFormInputValues={setFormInputValues}
              handleVehicleManuallChange={handleVehicleManuallChange}
              manualOverRideVin={!isVinAvailable}
            />
          )}
        </AdasBox>

        {/* Step 3 — Documents from client */}
        <AdasBox className='mb-4 rounded-xl border border-[#EBEDF0] bg-white px-6 py-5 shadow-sm'>
          <AdasBox className='mb-5 flex items-center gap-3 border-b border-[#EBEDF0] pb-4'>
            <StepBadge n={3} />
            <AdasTypography className='text-[15px] font-bold text-[#0A1628]'>Documents from client</AdasTypography>
            <span className='ml-auto text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>Optional</span>
          </AdasBox>
          <AdasBox className='flex flex-col gap-3'>
            <DocumentUploadRow
              name='preCalRepairEstimateScan'
              title='Collision repair estimate'
              subtitle='PDF from CCC, Mitchell, Audatex — used to reconstruct line items'
              existingData={documents.preCalRepairEstimateScan}
              onChange={documentChangeHandler}
            />
            <DocumentUploadRow
              name='calRequiredReport'
              title='Calibrations required report'
              subtitle='OEM scan tool report — confirms which calibrations the vehicle needs'
              existingData={documents.calRequiredReport}
              onChange={documentChangeHandler}
            />
          </AdasBox>
        </AdasBox>

        {/* Bottom action bar */}
        <AdasBox className='mt-6 flex flex-wrap items-center justify-end gap-3'>
          <AdasButton variant='outlined' aria-label='Cancel' onClick={() => history.goBack()} className='rounded-lg px-6 font-semibold'>
            CANCEL
          </AdasButton>
          <AdasButton
            disabled={!isEnable}
            variant='contained'
            aria-label='Create Work Order'
            isDebounceEnabled={true}
            onClick={() => submitHandler()}
            className='rounded-lg bg-[#0A1628] px-6 font-semibold text-white hover:bg-[#1F2D3D]'
          >
            CREATE WORK ORDER
          </AdasButton>
        </AdasBox>
        {renderVehicleInProgress()}
      </AdasBox>
    </AdasContainer>
  )
}
